/**
 * Thinking-effort metadata — which values each model accepts and what it
 * defaults to. Re-exported from `@/lib/model-registry`, which is where
 * every consumer imports it from.
 */

import { findCompanionModel } from "@/lib/companion-model-lookup";

/**
 * Which thinking-effort values a specific model accepts, and its default.
 *
 * Real provider values (no UX sentinels):
 *  - Claude Fable 5 / Sonnet 5 / Opus 4.8/4.7 → low | medium | high | xhigh | max
 *  - Claude Sonnet 4.6 → low | medium | high | max
 *  - Claude Haiku 4.5 → no effort support
 *  - Codex GPT-5.x → model-specific levels from minimal through ultra
 *    (Codex also accepts "none" — we omit from the picker)
 *  - OpenCode → no CLI-level effort flag today
 *
 * "Default" is a separate UX concept (see ``EffortChoice``): it means
 * "don't pin an effort — let the model use its own default".
 */
export type EffortValue =
  | "minimal"
  | "low"
  | "medium"
  | "high"
  | "xhigh"
  | "max"
  | "ultra";

/** UI choice: either a real effort value or "default" (no override). */
export type EffortChoice = EffortValue | "default";

export interface ModelEffortConfig {
  /** Accepted values in UI order. Empty = model does not support effort. */
  values: readonly EffortValue[];
  /** Default effort to pre-select when saving. Null when not supported. */
  default: EffortValue | null;
}

const CLAUDE_OPUS_EFFORT: ModelEffortConfig = {
  values: ["low", "medium", "high", "xhigh", "max"],
  default: "high",
};

const CLAUDE_ADAPTIVE_EFFORT: ModelEffortConfig = {
  values: ["low", "medium", "high", "xhigh", "max"],
  default: "high",
};

const CLAUDE_SONNET_EFFORT: ModelEffortConfig = {
  values: ["low", "medium", "high", "max"],
  default: "high",
};

const CODEX_GPT5_EFFORT: ModelEffortConfig = {
  values: ["minimal", "low", "medium", "high", "xhigh"],
  default: "medium",
};

const CODEX_GPT56_SOL_EFFORT: ModelEffortConfig = {
  values: ["low", "medium", "high", "xhigh", "max", "ultra"],
  default: "low",
};

const CODEX_GPT56_TERRA_EFFORT: ModelEffortConfig = {
  values: ["low", "medium", "high", "xhigh", "max", "ultra"],
  default: "medium",
};

const CODEX_GPT56_LUNA_EFFORT: ModelEffortConfig = {
  values: ["low", "medium", "high", "xhigh", "max"],
  default: "medium",
};

const CODEX_GPT5_NANO_EFFORT: ModelEffortConfig = {
  values: ["minimal", "low", "medium", "high"],
  default: "medium",
};

const NO_EFFORT: ModelEffortConfig = { values: [], default: null };

export const MODEL_EFFORT: Record<string, ModelEffortConfig> = {
  // Claude Fable 5 / Sonnet 5 — adaptive thinking, 5 levels
  "claude-fable-5-1": CLAUDE_ADAPTIVE_EFFORT,
  "claude-fable-5": CLAUDE_ADAPTIVE_EFFORT,
  sonnet: { ...CLAUDE_ADAPTIVE_EFFORT, default: "medium" },
  "sonnet[1m]": { ...CLAUDE_ADAPTIVE_EFFORT, default: "medium" },
  default: { ...CLAUDE_ADAPTIVE_EFFORT, default: "medium" },
  best: { ...CLAUDE_ADAPTIVE_EFFORT, default: "medium" },
  opus: { ...CLAUDE_ADAPTIVE_EFFORT, default: "medium" },
  "opus[1m]": { ...CLAUDE_ADAPTIVE_EFFORT, default: "medium" },
  opusplan: { ...CLAUDE_ADAPTIVE_EFFORT, default: "medium" },
  "claude-opus-5-5": { ...CLAUDE_ADAPTIVE_EFFORT, default: "medium" },
  "claude-sonnet-5-5": { ...CLAUDE_ADAPTIVE_EFFORT, default: "medium" },
  "claude-sonnet-5": { ...CLAUDE_ADAPTIVE_EFFORT, default: "medium" },
  // Claude Opus 4.8/4.7 — 5 levels
  "claude-opus-4-8": CLAUDE_OPUS_EFFORT,
  "claude-opus-4-8[1m]": CLAUDE_OPUS_EFFORT,
  "claude-opus-4-7": CLAUDE_OPUS_EFFORT,
  "claude-opus-4-7[1m]": CLAUDE_OPUS_EFFORT,
  // Claude Sonnet 4.6 — 4 levels (no xhigh)
  "claude-sonnet-4-6": CLAUDE_SONNET_EFFORT,
  "claude-sonnet-4-6[1m]": CLAUDE_SONNET_EFFORT,
  // Claude Haiku — no effort support
  haiku: NO_EFFORT,
  "claude-haiku-4-5": NO_EFFORT,
  "claude-haiku-4-5-20251001": NO_EFFORT,
  // Verified against ~/.codex/models_cache.json (Codex 0.160.1) on 2026-10-07.
  // Codex defaults Sol to low; Denker runs its default model at medium
  // (backend DEFAULT_THINKING_EFFORT).
  "gpt-6.1-sol": { ...CODEX_GPT56_SOL_EFFORT, default: "medium" },
  "gpt-6-sol": CODEX_GPT56_TERRA_EFFORT,
  "gpt-6-astra": CODEX_GPT56_TERRA_EFFORT,
  "gpt-6-luna": CODEX_GPT56_LUNA_EFFORT,
  // Codex GPT-5.6 family — model catalog omits minimal and adds max.
  "gpt-5.6-sol": CODEX_GPT56_SOL_EFFORT,
  "gpt-5.6-terra": CODEX_GPT56_TERRA_EFFORT,
  "gpt-5.6-luna": CODEX_GPT56_LUNA_EFFORT,
  // Earlier Codex GPT-5 family — minimal|low|medium|high|xhigh
  "gpt-5.5": CODEX_GPT5_EFFORT,
  "gpt-5.4": CODEX_GPT5_EFFORT,
  "gpt-5.4-pro": CODEX_GPT5_EFFORT,
  "gpt-5.4-mini": CODEX_GPT5_EFFORT,
  "gpt-5.4-nano": CODEX_GPT5_NANO_EFFORT,
  "gpt-5.3-codex": CODEX_GPT5_EFFORT,
  "gpt-5.3-codex-spark": CODEX_GPT5_NANO_EFFORT,
  "gpt-5.2": CODEX_GPT5_EFFORT,
  "gpt-5.2-codex": CODEX_GPT5_EFFORT,
  "gpt-5.1": CODEX_GPT5_EFFORT,
  "gpt-5.1-codex": CODEX_GPT5_EFFORT,
  "gpt-5.1-codex-max": CODEX_GPT5_EFFORT,
  "gpt-5.1-codex-mini": CODEX_GPT5_EFFORT,
  "gpt-5": CODEX_GPT5_EFFORT,
  "gpt-5-codex": CODEX_GPT5_EFFORT,
  "gpt-5-nano": CODEX_GPT5_NANO_EFFORT,
};

/**
 * Resolve the thinking-effort config for a given model id.
 *
 * Falls back to "no effort support" when the model is unknown — this keeps
 * the UI from offering a toggle that the backend would silently ignore.
 */
export function getModelEffortConfig(
  modelId: string | undefined | null,
): ModelEffortConfig {
  if (!modelId) return NO_EFFORT;
  // Prefer companion-reported reasoning_efforts. When the adapter
  // supplies a non-empty list, trust it — filter to the UI-known
  // vocabulary and derive a default. Empty array means the model has
  // no reasoning knob; fall through to the hardcoded map only when
  // the companion hasn't reported anything at all.
  const match = findCompanionModel(modelId);
  if (match?.reasoning_efforts && match.reasoning_efforts.length > 0) {
    const allowed: readonly EffortValue[] = [
      "minimal",
      "low",
      "medium",
      "high",
      "xhigh",
      "max",
      "ultra",
    ];
    const values = match.reasoning_efforts.filter((e): e is EffortValue =>
      (allowed as readonly string[]).includes(e),
    );
    if (values.length > 0) {
      // Pick a sensible default from the hardcoded map when the
      // model id matches, else fall back to medium/high/first.
      const hardcoded = MODEL_EFFORT[modelId]?.default;
      const defaultValue: EffortValue =
        (hardcoded && values.includes(hardcoded) && hardcoded) ||
        (values.includes("high") && "high") ||
        (values.includes("medium") && "medium") ||
        values[0]!;
      return { values, default: defaultValue };
    }
  }
  return MODEL_EFFORT[modelId] ?? NO_EFFORT;
}

/** Convenience: does this model support any thinking-effort knob? */
export function modelSupportsThinkingEffort(
  modelId: string | undefined | null,
): boolean {
  return getModelEffortConfig(modelId).values.length > 0;
}
